import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import ReCAPTCHA from 'react-google-recaptcha'
import { HiOutlineEye, HiOutlineEyeSlash } from 'react-icons/hi2'
import { message } from 'antd'

import './styles.scss'
import OTPCard from './otp'
import uris from '../../config/uris'
import { LoginAction, LoginReset } from '../../store/slices/auth/loginSlice'
import { OtpAction } from '../../store/slices/auth/otpSlice'
import LogoutSession from './logout-session'
import ForgotPasswordCard from './forgot-password'
import { encrypt_forge } from '../../helpers/encrypt/forge-encrypt'
import { generateKey } from '../../helpers/encrypt/crypto-encrypt'

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [emailError, setEmailError] = useState(" ");
  const [passwordError, setPasswordError] = useState(" ");
  const [captchaError, setCaptchaError] = useState(" ");
  const [showCaptcha, setShowCaptcha] = useState(true);
  const [showOTPScreen, setShowOTPScreen] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpPopup, enableOtpPopup] = useState(false);
  const [otpFlag, setOtpFlag] = useState(false);
  const [showConfirmCard, setShowConfirmCard] = useState(false);
  const [showPassword, setShowPassword] = useState(false)
  const isDevPlatform = process.env.REACT_APP_ENVIRONMENT === "dev" || process.env.REACT_APP_ENVIRONMENT === "local";

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { login, isLoading: loginLoading, error: loginError } = useSelector((state) => state.login)
  const { otp: otpDetails, isLoading: otpLoading, error: otpError } = useSelector((state) => state.otp)
  const { user } = otpDetails
  const captchaRestrictedMailSuffix = ["zkb.ch"]

  const validateEmail = (emailmsg) => {
    const regex = /\S+@\S+\.\S+/
    return regex.test(emailmsg)
  }

  const onEmailChange = (e) => {
    if (e.target.value.match(/^[a-zA-Z0-9_@./#&+-]*$/)) {
      setEmail(e.target.value);
    }
  };

  const onPasswordChange = (e) => {
    if (e.target.value.match(/^[a-zA-Z0-9_\W]*$/)) {
      setPassword(e.target.value)
    }
  };  
  
  const handleLogin = () => {
    if(loginLoading) return;
    const validEmail = validateEmail(email)
    const isCaptchaAvailable = showCaptcha ? isChecked : true

    if (validEmail && password && isCaptchaAvailable) {
      const login = { email, password }
      let user = encrypt_forge(login)
      const loginDetails = {
        login: user,
      }

      dispatch(LoginAction(loginDetails))
      enableOtpPopup(true)
    } else {
      !validEmail ? setEmailError('*Enter a valid email') : setEmailError('')
      !password ? setPasswordError('*Enter a valid password') : setPasswordError('')
      !isChecked ? setCaptchaError('*Please verify that you are not a robot.') : setCaptchaError('')
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loginLoading) {
      handleLogin()
    }
  }

  const handleOtpChange = () => {
    if (otpValue?.length === 4) {
      const login = { email, otp: otpValue }
      let user = encrypt_forge(login)
      const otpDetails = {
        login: user,
      }
      dispatch(OtpAction(otpDetails))
      setOtpFlag(true)
    }
    setOtpValue('')
  }

  const onCaptchaChange = (e) => {
    setIsChecked(true)
  }

  const handleForgotPassword = () => {
    setShowForgotPassword(true)
  }

  const handleSignUpNavigate = () => {
    navigate(uris.register)
  }

  useEffect(() => {
    if (login && otpPopup) {
      enableOtpPopup(false)
      setShowOTPScreen(true)
    }
  }, [login])

  useEffect(() => {
    if (otpError?.message) {
      message.error(otpError?.message)
      setShowOTPScreen(false)
    }
  }, [otpError?.message])

  useEffect(() => {
    if (user?._id && otpFlag) {
      setOtpFlag(false)
      setShowOTPScreen(false)
      navigate(uris?.companyResearch)
    }
  }, [user])

  useEffect(() => {
    if (login?.message) {
      dispatch(LoginReset())
      setShowConfirmCard(false)
    }
  }, [login?.message])

  useEffect(() => {
    if (loginError?.confirmationRequired) {
      setShowConfirmCard(true)
    } else if (loginError?.message) {
      message.error(loginError?.message)
    }
  }, [loginError])

  useEffect(() => {
    if (otpValue?.length === 4) {
      handleOtpChange()
    }
  }, [otpValue])

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const sessionExpired = queryParams.get('sessionExpired') === 'true'

    if (sessionExpired) {
      message.error('Session Expired!')
      navigate(uris.login, { replace: true })
    }
  }, [location.search])

  useEffect(() => {
    if (email) {
      const mailSuffix = email?.split("@")[1]
      const isRestrictCaptcha = captchaRestrictedMailSuffix?.includes(mailSuffix?.toLocaleLowerCase() || "")

      setShowCaptcha(!isRestrictCaptcha)
      setCaptchaError(" ")
    }
  }, [email])

  useEffect(() => {
    if(isDevPlatform){
      setIsChecked(true);
    }
  },[isDevPlatform])

  return (
    <div className="login-page">
      {!showOTPScreen && !showForgotPassword && !showConfirmCard && (
        <div className="login-form-container">
          <div className="form-contents">
            <div className="email-section">
              <div className="label">Email</div>
              <div className="input-area">
                <div className="overlap-group">
                  <input className="form-input" name="login" type="text" placeholder="Enter your Email Id" onChange={onEmailChange} value={email} onKeyDown={handleKeyDown} />
                </div>
              </div>
              <div className="error-message">{emailError}</div>
            </div>
            <div className="password-section">
              <div className="label">Password</div>
              <div className="input-area">
                <div className="overlap-group">
                  <input
                    className="form-input"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    onChange={onPasswordChange}
                    value={password}
                    onKeyDown={handleKeyDown}
                  />
                  <div className="password-hide-icon" onClick={() => setShowPassword((prev) => !prev)}>
                    {showPassword ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
                  </div>
                </div>
              </div>
              <div className="error-message">{passwordError}</div>
              <div className="forgot-password-text" onClick={() => handleForgotPassword()}>
                Forgot your password?
              </div>
            </div>
            {showCaptcha && process.env.REACT_APP_ENVIRONMENT === "production" && (
              <div className="re-captcha-section">
                <ReCAPTCHA
                  name="verified"
                  className={`recaptcha ${isChecked ? 'red-class' : ''}`}
                  sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA}
                  onChange={(e) => onCaptchaChange(e)}
                  size="normal"
                  theme="light"
                  onExpired={() => setIsChecked(false)}
                  onError={() => setIsChecked(false)}
                />
                {captchaError && <div className="error-message">{captchaError}</div>}
              </div>
            )}
            <div className="form-button" onClick={handleLogin}>
              <div className="text-wrapper">{loginLoading ? 'Loading...' : 'Login'}</div>
            </div>
            <div className="sign-up-section">
              <div className="text-wrapper">Not registered yet?</div>
              <div className="register-text" onClick={() => handleSignUpNavigate()}>
                Register here
              </div>
            </div>
          </div>
        </div>
      )}

      {showOTPScreen && (
        <div className="auth-modal-container otp-screen">
          <OTPCard email={email} otp={otpValue} setOtp={setOtpValue} showOtpScreen={setShowOTPScreen} handleOtpChange={handleOtpChange} loading={otpLoading} />
        </div>
      )}

      {showConfirmCard && (
        <div className="auth-modal-container logout-session-screen">
          <LogoutSession setShowConfirmCard={setShowConfirmCard} userDetails={{ email, password }} isLoading={loginLoading} />
        </div>
      )}

      {showForgotPassword && (
        <div className="auth-modal-container forgot-password-screen">
          <ForgotPasswordCard setShowForgotPassword={setShowForgotPassword} />
        </div>
      )}
    </div >
  )
}

export default Login
